import type { DataTableColumn } from "@etudescarre/ui";
import { isNotNil } from "@etudescarre/utils";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router";
import { apiClient } from "@/lib/api/client";
import { formatDay } from "@/lib/utils/date";
import { Classeur, type FilterDef } from "../components/classeur";
import { ClasseurPdfButton } from "../components/classeur/classeur-pdf-button";
import {
  useClasseurFilters,
  useClasseurState,
} from "../components/classeur/use-classeur-data";

type ReportItem = {
  id: string;
  numero: string | null;
  dateReception: string | null;
  dateRedaction: string | null;
  contractName: string | null;
  authorEmployee: string | null;
  noticeCount: number;
  fichierId: number | null;
};

const EMETTEUR_FILTER_KEY = "emetteur";

const columns: DataTableColumn<ReportItem>[] = [
  { id: "contractName", header: "Contrat", accessor: "contractName" },
  { id: "numero", header: "N°", accessor: "numero" },
  { id: "authorEmployee", header: "Auteur", accessor: "authorEmployee" },
  {
    id: "dateReception",
    header: "Date réception",
    accessor: "dateReception",
    cell: ({ value }) => (value ? formatDay(value as string) : "-"),
  },
  {
    id: "dateRedaction",
    header: "Date rédaction",
    accessor: "dateRedaction",
    cell: ({ value }) => (value ? formatDay(value as string) : "-"),
  },
  { id: "noticeCount", header: "Avis", accessor: "noticeCount" },
  {
    id: "pdf",
    header: "PDF",
    accessor: "fichierId",
    cell: ({ row }) => (
      <ClasseurPdfButton
        fichierId={row.fichierId}
        label={`le rapport ${row.numero ?? ""}`}
      />
    ),
  },
];

export function ClasseurRapports() {
  const { yardId } = useParams<{ yardId: string }>();
  const [state, setState] = useClasseurState<ReportItem>();

  const filters = useMemo<FilterDef[]>(
    () => [
      {
        key: EMETTEUR_FILTER_KEY,
        label: "Contrat émetteur",
        options: [
          ...new Set(
            state.items.map((item) => item.contractName).filter(isNotNil),
          ),
        ].map((name) => ({ value: name, label: name })),
      },
    ],
    [state.items],
  );
  const { activeFilters, onFilterChange } = useClasseurFilters({ filters });
  const emetteur = activeFilters[EMETTEUR_FILTER_KEY]?.[0];
  const items = state.items.filter(
    (item) => !isNotNil(emetteur) || item.contractName === emetteur,
  );

  useEffect(() => {
    async function fetchData() {
      setState((prev) => ({ ...prev, isLoading: true }));
      const { data } = await apiClient.GET("/api/yards/{yardId}/reports", {
        params: {
          path: { yardId: yardId ?? "" },
          query: { direction: "received" },
        },
      });
      if (data) {
        const items = data.data.items.map((item) => ({
          ...item,
          id: String(item.id),
        }));
        setState({ items, total: data.data.total, isLoading: false });
      } else {
        setState({ items: [], total: 0, isLoading: false });
      }
    }
    fetchData();
  }, [yardId, setState]);

  return (
    <Classeur
      title="Classeur rapports d'examen"
      items={items}
      columns={columns}
      filters={filters}
      activeFilters={activeFilters}
      onFilterChange={onFilterChange}
      isLoading={state.isLoading}
      emptyMessage="Aucun rapport d'examen"
      total={items.length}
    />
  );
}
